
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { useAuth, withForcePlanToLoggedInUnsubs } from 'context';
import { basicSEO } from 'helpers/jsx';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import AppLink from '../../../components/AppLink/AppLink';
import CategorySectionFull from '../../../components/CategorySections/CategorySectionFull';
import Layout from '../../../components/Layout';
import { Namespace } from '../../../enums/namespaces';
import { PlayBase, Theatre as TheatreApi } from '../../../typings/api';
import { StaticPathParams } from '../../../typings/staticPaths';
import Theatre from 'models/Theatre';
import { createRedirectIfNeeded, Permalink } from 'helpers';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { useLang } from 'context/langContext';
import { baseUrl } from 'config/routeTable';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { locales } from 'constants/lang';

interface Props {
  theatre: TheatreApi;
  countryCode: string;
  isUserLoggedIn: boolean;
  canonical: string;
}

const TheatreDetail: NextPage<Props> = ({ theatre, countryCode, isUserLoggedIn, canonical }) => {
  const { t } = useTranslation(Namespace.Theaters);
  const { locale } = useLang();
  const router = useRouter();
  const { user, isUserLoaded } = useAuth();

  const showDonateButton = useMemo(() => {
    return (isUserLoggedIn || (isUserLoaded && user)) && theatre?.donations_allowed;
  }, [isUserLoggedIn, isUserLoaded, user, theatre]);

  const showRegisterButton = useMemo(() => {
    return isUserLoaded && !isUserLoggedIn;
  }, [isUserLoaded, isUserLoggedIn]);

  const structuredSEOData = useMemo(() => {
    return {
      '@context': 'https://schema.org/',
      '@type': 'EntertainmentBusiness',
      name: theatre.name,
      image: theatre.poster_url,
      url: `${baseUrl(router.locale)}${router.asPath}`,
      sameAs: theatre.www_url ? [theatre.www_url] : undefined,
    };
  }, [theatre]);

  return (
    <Layout countryCode={countryCode}>
      <Head>
        {basicSEO(
          t('seo:theatre_title', { name: theatre.name }),
          theatre.about,
          theatre.poster_url,
          undefined,
          true,
          canonical,
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredSEOData),
          }}
        ></script>
        {locales.map((langLocale) => (
          <link
            key={langLocale}
            rel="alternate"
            href={`${baseUrl(langLocale)}${Permalink.theatre(theatre, langLocale)}`}
            hrefLang={langLocale}
          />
        ))}
        <link
          key="lang-default"
          rel="alternate"
          href={`${baseUrl('en')}${Permalink.theatre(theatre, 'en')}`}
          hrefLang="x-default"
        />
      </Head>
      <main className="author-detail theater-detail">
        <div className="info-wrapper">
          <div className="image-placeholder">
            {theatre?.poster_url ? <img src={theatre?.poster_url} alt={theatre.name} /> : theatre?.name?.substr(0, 1)}
          </div>
          <div className="text-wrapper">
            <h1>{theatre?.name}</h1>
            <div className="buttons">
              {showDonateButton && (
                <AppLink href={Permalink.donateTheatre(theatre, locale)}>
                  <a className="btn btn-primary">{t('support')}</a>
                </AppLink>
              )}
              {showRegisterButton && (
                <AppLink href={Permalink.registerStep1(locale)}>
                  <a className="btn btn-primary">{t('register')}</a>
                </AppLink>
              )}

              {theatre?.www_url ? (
                <a className="btn btn-outline-info" href={theatre.www_url} rel="external" target="_blank">
                  {t('web_page')}
                </a>
              ) : null}
            </div>
            <p>{theatre?.about}</p>
          </div>
        </div>
        <CategorySectionFull
          plays={theatre?.plays as PlayBase[]}
          theatre={theatre?.name}
          headline={t('theatre_plays')}
        />
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  let slug = ctx.params?.slug;
  const { locale, apiClient, countryCode, user, hostLocale, host } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  try {
    slug = slug.replace('NaN-', '');
    const [id, ..._slug] = slug.split('-');
    let res: TheatreApi;

    // old urls without ids
    if (isNaN(+id)) {
      res = await apiClient.getTheatreDetail(slug);
    } else {
      res = await apiClient.getTheatreDetail(+id);
    }

    const theatre = Theatre.fromJSON(res);

    const tempTheatre = { ...theatre };
    tempTheatre.name = tempTheatre.names?.find((n) => n.language === hostLocale)?.value || theatre.name;
    const canonical = `https://${host}${Permalink.theatre(tempTheatre, hostLocale)}`;

    const localisedSlug = theatre.localisedSlug(locale);

    if (_slug.join('-') !== localisedSlug) {
      return {
        redirect: {
          permanent: true,
          destination: Permalink.theatre(theatre, locale),
        },
      };
    }

    res.plays = theatre.plays.map((p) =>
      Object.assign({}, p, { theatre: { id: theatre.id, name: theatre.name, names: theatre.names } }),
    );

    return {
      props: { theatre: res, countryCode, isUserLoggedIn: !!user, canonical },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
}

export default withForcePlanToLoggedInUnsubs(TheatreDetail);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/theatres/[slug]/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  